import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@material-ui/core";
import { buildProposalHTMLv2 } from "src/utils/buildProposalHTMLv2";

// Debounce que captura cambios profundos en arrays/objetos: useState con
// JSON.stringify como dep del effect, JSON.parse al asignar el valor.
function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  const serialized = JSON.stringify(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(JSON.parse(serialized)), delay);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized, delay]);
  return debounced;
}

const ProposalPreview = ({ propuesta, fases, equipo }) => {
  const p = useDebounce(propuesta, 300);
  const f = useDebounce(fases, 300);
  const e = useDebounce(equipo, 300);

  const html = useMemo(
    () =>
      buildProposalHTMLv2({
        propuesta: p,
        fases: f,
        equipo: e,
        // El preview siempre se renderiza pre-envío (BORRADOR), donde aún
        // no existe el token JWT que firma la URL del QR. El builder maneja
        // qrPortalSVG=null omitiendo el bloque del QR completamente.
        qrPortalSVG: null,
        mode: "completo",
      }),
    [p, f, e],
  );

  const iframeRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);

  // Captura el scrollY del iframe ANTES de que cambie el srcDoc, para
  // restaurarlo cuando el documento nuevo termine de cargar. Evita que
  // cada re-render lance al usuario al inicio del preview.
  useEffect(() => {
    const node = iframeRef.current;
    if (!node) return;
    setScrollY(node.contentWindow?.scrollY ?? 0);
  }, [html]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ fontStyle: "italic" }}
      >
        El PDF descargable agrega numeración y header corporativo en cada
        página.
      </Typography>
      <Box
        sx={{
          borderRadius: 1,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <iframe
          ref={iframeRef}
          title="Vista previa de la propuesta"
          srcDoc={html}
          style={{
            width: "100%",
            height: "76vh",
            border: "none",
            display: "block",
          }}
          onLoad={() => {
            const win = iframeRef.current?.contentWindow;
            if (win && scrollY > 0) win.scrollTo(0, scrollY);
          }}
        />
      </Box>
    </Box>
  );
};

ProposalPreview.propTypes = {
  propuesta: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    titulo: PropTypes.string,
    descripcion: PropTypes.string,
    antecedentes: PropTypes.string,
    alcance: PropTypes.string,
    resumenEjecutivo: PropTypes.string,
    introduccion: PropTypes.string,
    condicionesPago: PropTypes.string,
    duracionEstimada: PropTypes.string,
    proximosPasos: PropTypes.string,
    clausulaConfidencialidad: PropTypes.string,
    fecha: PropTypes.string,
    vendedorCorreo: PropTypes.string,
    vendedorTelefono: PropTypes.string,
    nombreSolicitante: PropTypes.string,
    correoSolicitante: PropTypes.string,
    telefonoSolicitante: PropTypes.string,
  }),
  fases: PropTypes.arrayOf(
    PropTypes.shape({
      numero: PropTypes.number,
      titulo: PropTypes.string,
      objetivo: PropTypes.string,
      actividades: PropTypes.string,
      entregable: PropTypes.string,
      duracion: PropTypes.string,
      monto: PropTypes.number,
      tipoMoneda: PropTypes.string,
      refCliente: PropTypes.string,
      nombreCliente: PropTypes.string,
    }),
  ),
  equipo: PropTypes.arrayOf(
    PropTypes.shape({
      nombre: PropTypes.string,
      rolDisplay: PropTypes.string,
      descripcionExperiencia: PropTypes.string,
      fotoUrl: PropTypes.string,
    }),
  ),
};

ProposalPreview.defaultProps = {
  propuesta: {},
  fases: [],
  equipo: [],
};

export default ProposalPreview;
